import { message, Select, Skeleton, Space } from 'antd';
import Form, { FormInstance } from 'antd/lib/form';
import FormItem, { FormItemProps } from 'antd/lib/form/FormItem';
import * as React from 'react';
import { useImperativeHandle, useState } from 'react';
import DistrictApiService from '../../../api/DistrictApiService';
import DistrictUserApiService from '../../../api/DistrictUserApiService';
import RoleType from '../../../consts/RoleType';
import * as AddDistrictToUserHandler from '../../../handlerModels/AddDistrictToUserHandler';
import * as GetAllDistrictsHandler from '../../../handlerModels/GetAllDistrictsHandler';
import DistrictDTO from '../../../models/DistrictDTO';
import RoleDTO from '../../../models/RoleDTO';
import Validator from '../../../models/Validator';
import BaseFormProps from '../../../redux/bases/BaseFormProps';
import Guid from '../../../utils/Guid';
import LookupsUtil from '../../../utils/LookupsUtil';
import ValidationRuleUtil from '../../../utils/ValidationRuleUtil';
import ValidationUtil from '../../../utils/ValidationUtil';
import Dropdown from '../../inputs/Dropdown';


interface AddDistrictToUserFormProps extends BaseFormProps {
  userId: string;
  onSubmit?: () => void;
  resetForm?: (value: string) => void;
}

const AddDistrictToUserForm = React.forwardRef((props: AddDistrictToUserFormProps, ref) => {
  const _formRef = React.createRef<FormInstance>();
  const _formItems = new Map<string, FormItemProps>()
    .set(AddDistrictToUserHandler.Request.districtIdentifier, {
      label: 'District',
      required: true,
      name: AddDistrictToUserHandler.Request.districtIdentifier,
      rules: [
        ValidationRuleUtil.required()
      ]
    })
    .set(AddDistrictToUserHandler.Request.roleIds, {
      label: 'District Roles',
      required: true,
      name: AddDistrictToUserHandler.Request.roleIds,
      rules: [
        ValidationRuleUtil.required()
      ]
    });

  useImperativeHandle(ref, () => ({
    handleSubmit: () => handleSubmit(),
    reset: () => reset()
  }));

  const reset = () => {
    setFieldErrors(null);
    setSubmitted(false);
    _formRef.current?.resetFields();
  }

  const [loading, setLoading] = useState(true);
  const [, setError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({} as ({ [key: string]: Validator[]; } | null));
  const [submitted, setSubmitted] = useState(false);
  const [districts, setDistricts] = useState([] as DistrictDTO[]);
  const [roles, setRoles] = useState([] as RoleDTO[]);

  React.useEffect(() => {
    fetchData()
  }, []);

  const fetchData = () => {
    setLoading(true);
    const loaders = [];

    if (!roles || roles.length == 0) {
      loaders.push(loadRoles());
    }

    if (!districts || districts.length == 0) {
      loaders.push(loadDistricts());
    }

    Promise.all(loaders).then(() => {
      setLoading(false);
      if (props.resetForm) {
        props.resetForm;
      }
    });
  }

  const loadDistricts = () => {
    const request = GetAllDistrictsHandler.Request.create({
    });

    return DistrictApiService.getAllDistricts(request)
      .then((results: GetAllDistrictsHandler.Result) => {
        if (results) {
          setDistricts(results.districts ?? []);
        }
      })
      .catch(() => {
        setError(true);
      });
  }


  const loadRoles = () => {
    return LookupsUtil.getAll<RoleDTO>(RoleDTO.className)
      .then((results: RoleDTO[]) => {
        if (results) {
          setRoles(results);
        }
      }).catch(() => {
        setError(true);
      });
  }

  const renderRole = (role: RoleDTO) => {
    if (role.id && role.roleTypeId == RoleType.DISTRICT) {
      return <Select.Option key={role.id ?? Guid.Empty()} value={role.id ?? Guid.Empty()}> {role.name}</Select.Option>
    }
  }

  const handleSubmit = () => {
    setSubmitted(true);

    const form = (_formRef ? (_formRef.current as any).getFieldsValue() : null);
    const request = AddDistrictToUserHandler.Request.create({
      userId: props.userId,
      districtIdentifier: form.districtIdentifier,
      roleIds: form.roleIds
    });

    DistrictUserApiService.addDistrictToUser(request)
      .then((result: AddDistrictToUserHandler.Result) => {
        setSubmitted(true);
        if (result?.succeeded) {
          message.success('District Added');
          setFieldErrors(null);
          setSubmitted(false);

          if (props.onSubmit) {
            props.onSubmit();
          }
        }
        else {
          setError(!result?.succeeded);
          setFieldErrors(result?.fieldErrors);
          message.error('District could not be added');
        }
      })
      .catch(() => {
        setError(true);
        message.error('District could not be added');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const renderDistrict = (district: DistrictDTO) => {
    if (district.id) {
      return <Select.Option key={district.districtId ?? 0} value={district.districtId ?? 0}> {district.name}</Select.Option>
    }
  }

  if (loading) {
    return <Skeleton active />
  }
  else {

    return (
      <Form
        ref={_formRef}
        layout="vertical"
        requiredMark={true}>
        <Space size="small" direction="vertical">
          <FormItem
            {..._formItems.get(AddDistrictToUserHandler.Request.districtIdentifier)}
            {...ValidationUtil.getValidation(AddDistrictToUserHandler.Request.districtIdentifier, fieldErrors, submitted)}  >
            <Dropdown dropdownMatchSelectWidth={false}
              showSearch
              optionFilterProp="children"
            >
              {districts.map(x => { return renderDistrict(x) })}
            </Dropdown>
          </FormItem>

          <FormItem
            {..._formItems.get(AddDistrictToUserHandler.Request.roleIds)}
            {...ValidationUtil.getValidation(AddDistrictToUserHandler.Request.roleIds, fieldErrors, submitted)}  >
            <Dropdown dropdownMatchSelectWidth={false}
              mode="multiple">
              {roles.map(x => { return renderRole(x) })}
            </Dropdown>
          </FormItem>
        </Space >
      </ Form>
    );
  }

})

AddDistrictToUserForm.displayName = 'AddDistrictToUserForm'
export default AddDistrictToUserForm;